import {
    CouponDiscountType,
    CouponProviderType,
    DiscountCoupon,
    DiscountCouponType,
    DiscountPolicy,
    DiscountPolicyType,
    ExclusiveCouponType,
} from '@web/core/constants'

type OrderSheetCoupon = {
    /** 쿠폰 번호 */
    couponId: string
    /** 쿠폰 이름 */
    couponName: string
    /** 쿠폰 발행 번호 */
    couponPublicationId: string
    /** 유효기간 시작 일시 */
    validityPeriodStartDateTime: string
    /** 유효기간 종료 일시 */
    validityPeriodEndDateTime: string
    /** 할인 방식 유형 (정률/정액) */
    discountUnitType: DiscountPolicyType
    /** 할인 값 */
    discountValue: number
    /** 최대 할인 금액 */
    maxDiscountAmount: Maybe<number>
    /** 최소 주문 금액 */
    minOrderAmount: Maybe<number>
    /** 쿠폰 비활성화 여부 */
    disabled: boolean
    /** 쿠폰 발행처 유형 */
    couponProviderType: CouponProviderType
    /** 쿠폰 할인 유형 */
    couponDiscountType: CouponDiscountType
    /** FE 쿠폰 구분 Type */
    couponType: DiscountCouponType
    /** 전용 쿠폰 유형 */
    exclusiveCouponType?: ExclusiveCouponType
    /** 앱전용 쿠폰 유형 */
    locationType?: 'ALL' | 'SHOPPING_APP'
}

type CommonCouponView = Maybe<{
    /** 쿠폰 적용 타입 */
    couponType: DiscountCouponType
    /** 할인 방식 유형 (정율/정액) */
    discountUnitType: DiscountPolicyType
    /** 할인 값 */
    discountValue: number
    /** 최대 할인 금액 */
    maxDiscountAmount: Maybe<number>
}>

/**
 * 쿠폰의 할인 금액을 처리합니다.
 */
export const getCouponDiscountValue = (coupon: CommonCouponView, price: number): number => {
    if (!coupon) {
        return 0
    }
    if (isFinite(price)) {
        if (coupon.discountUnitType === DiscountPolicy.RATIO) {
            /** % 일때, 최대 금액보다 크다면, 최대 금액을 반환한다. */
            const discountValue = Math.ceil(((price / 100) * coupon.discountValue) / 10) * 10
            if (coupon.maxDiscountAmount && discountValue > coupon.maxDiscountAmount) {
                return coupon.maxDiscountAmount
            }
            return discountValue
        }
        if (coupon.discountUnitType === DiscountPolicy.AMOUNT) {
            /** 만약 정률 값보다 금액이 작다면, 금액만 반영 */
            return Math.min(price, coupon.discountValue || 0)
        }
    }
    return coupon.discountValue || 0
}

/** 할인 정보 생성 */
const createDiscountAmount = (
    list: DiscountCouponType[],
    getDiscount: (coupon: CommonCouponView, price: number) => number,
) => {
    return ({amount, coupons}: {amount: number; coupons: CommonCouponView[]}): number => {
        let remainPrice = amount
        return list
            .reduce<CommonCouponView[]>((acc, couponType) => {
                const item = coupons.find((coupon) => coupon?.couponType === couponType)
                if (item) {
                    return [...acc, item]
                }
                return acc
            }, [])
            .reduce<number>((acc, coupon) => {
                const discount = getDiscount(coupon, remainPrice)
                remainPrice -= discount
                return acc + discount
            }, 0)
    }
}

/** 할인 정보 생성 */
const createDiscountInfo = (
    list: DiscountCouponType[],
    getDiscount: (coupon: OrderSheetCoupon, price: number) => number,
) => {
    return ({amount, coupons}: {amount: number; coupons: OrderSheetCoupon[]}) => {
        let remainPrice = amount

        return list.reduce<
            {
                discountAmount: number
                discountType: CouponDiscountType
                couponType: DiscountCouponType
                discountProviderType: CouponProviderType
            }[]
        >((acc, couponType) => {
            const targetCoupon = coupons.find((coupon) => coupon?.couponType === couponType)

            if (!targetCoupon) {
                return acc
            }

            const discountAmount = getDiscount(targetCoupon, remainPrice)
            remainPrice -= discountAmount

            return [
                ...acc,
                {
                    discountAmount,
                    discountType: targetCoupon.couponDiscountType,
                    couponType: targetCoupon.couponType,
                    discountProviderType: targetCoupon.couponProviderType,
                },
            ]
        }, [])
    }
}

/**
 * 상품 결제 할인금액
 * ((단가 + 옵션가) X 수량) - (상품단가할인 X 할인적용개수) - (복수 구매 할인 X 할인 적용개수) - (판매가 할인 금액)
 * - (판매가 할인 금액) : 직전 할인 적용 후 잔여금액을 다음 할인의 기준 금액으로 처리
 *
 * 상품/스토어/주문 할인쿠폰 계산 정책
 * @ref https://wiki.navercorp.com/pages/viewpage.action?pageId=412606321
 */
export const getProductItemCouponDiscount = ({
    salePrice,
    optionPrice = 0,
    quantity,
    coupons,
}: {
    // 단가
    salePrice: number
    // 옵션가
    optionPrice: number
    // 수량
    quantity: number
    coupons: CommonCouponView[]
}): number => {
    const totalImmediateDiscount = createDiscountAmount(
        [
            // 1. 판매자 즉시할인
            DiscountCoupon.SELLER_IMMEDIATE_DISCOUNT,
            // 2. 관리자 즉시할인
            DiscountCoupon.MANAGER_IMMEDIATE_DISCOUNT,
        ],
        getCouponDiscountValue,
    )({
        coupons,
        amount: salePrice,
    })

    // 단가할인 상품주문금액 (단가 + 옵션가 - 상품단가 할인금액) X 할인적용개수
    const orderAmount = (salePrice + optionPrice - totalImmediateDiscount) * quantity

    // 판매가 할인 금액
    const totalDiscountAmount = createDiscountAmount(
        [
            // 3. 상품할인 (판매자 상품할인)
            DiscountCoupon.SELLER_PRODUCT_DISCOUNT,
            // 4. 판매자 스토어찜 할인
            DiscountCoupon.SUBSCRIBER_DISCOUNT,
            // 5. 네이버 추가 할인 (관리자 상품할인)
            DiscountCoupon.MANAGER_PRODUCT_DISCOUNT,
            // 6. 상품추가할인 (판매자/관리자 중복할인)
            DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT,
        ],
        getCouponDiscountValue,
    )({
        coupons,
        amount: orderAmount,
    })

    return totalImmediateDiscount + totalDiscountAmount
}

export const getProductItemCouponDiscounts = ({
    orderAmount = 0,
    coupons,
}: {
    orderAmount: number
    coupons: OrderSheetCoupon[]
}) => {
    return createDiscountInfo(
        [
            // 3. 상품할인 (판매자 상품할인)
            DiscountCoupon.SELLER_PRODUCT_DISCOUNT,
            // 4. 판매자 스토어찜 할인
            DiscountCoupon.SUBSCRIBER_DISCOUNT,
            // 5. 네이버 추가 할인 (관리자 상품할인)
            DiscountCoupon.MANAGER_PRODUCT_DISCOUNT,
            // 6. 상품추가할인 (판매자/관리자 중복할인)
            DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT,
        ],
        getCouponDiscountValue,
    )({
        coupons,
        amount: orderAmount,
    })
}

const ErrorMessage =
    '선택하신 쿠폰 적용 시, 주문금액이 0원 미만이 되어 기존 선택된 쿠폰 중 일부 쿠폰을 적용할 수 없습니다.'

export const getCouponDiscountValueByC1 = (coupon: CommonCouponView, price: number): number => {
    if (!coupon) {
        return 0
    }
    if (isFinite(price)) {
        if (coupon.discountUnitType === DiscountPolicy.RATIO) {
            if (price <= 0) {
                throw new Error(ErrorMessage)
            }
            /** % 일때, 최대 금액보다 크다면, 최대 금액을 반환한다. */
            const discountValue = Math.ceil(((price / 100) * coupon.discountValue) / 10) * 10
            if (coupon.maxDiscountAmount && discountValue > coupon.maxDiscountAmount) {
                return coupon.maxDiscountAmount
            }
            return discountValue
        }
        if (coupon.discountUnitType === DiscountPolicy.AMOUNT) {
            if (price - (coupon.discountValue || 0) < 0) {
                throw new Error(ErrorMessage)
            }
            /** 만약 정률 값보다 금액이 작다면, 금액만 반영 */
            return Math.min(price, coupon.discountValue || 0)
        }
    }
    return coupon.discountValue || 0
}

export function getProductItemCouponDiscountByC1({
    orderAmount = 0,
    coupons,
}: {
    orderAmount: number
    coupons: CommonCouponView[]
}): number {
    // 판매가 할인 금액
    const totalDiscountAmount = createDiscountAmount(
        [
            // 3. 상품할인 (판매자 상품할인)
            DiscountCoupon.SELLER_PRODUCT_DISCOUNT,
            // 4. 판매자 스토어찜 할인
            DiscountCoupon.SUBSCRIBER_DISCOUNT,
            // 5. 네이버 추가 할인 (관리자 상품할인)
            DiscountCoupon.MANAGER_PRODUCT_DISCOUNT,
            // 6. 상품추가할인 (판매자/관리자 중복할인)
            DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT,
        ],
        getCouponDiscountValueByC1,
    )({
        coupons,
        amount: orderAmount,
    })

    return totalDiscountAmount
}
